import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { 
  Building2, 
  Plus, 
  Search,
  Users,
  Phone,
  Mail,
  Edit,
  Trash2,
  Loader2,
  Calendar
} from 'lucide-react';

export default function ClientsPage() {
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    client_code: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const resetForm = () => {
    setFormData({
      name: '',
      client_code: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: ''
    });
    setFormErrors({});
    setEditingClient(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Client name is required';
    }
    if (!formData.client_code.trim()) {
      errors.client_code = 'Client code is required';
    } else if (formData.client_code.length > 10) {
      errors.client_code = 'Client code must be 10 characters or less';
    }
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      errors.contact_email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        client_code: formData.client_code.toUpperCase().trim(),
        contact_name: formData.contact_name.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        contact_phone: formData.contact_phone.trim() || null,
        address: formData.address.trim() || null,
      };

      console.log('Submitting client:', payload);

      if (editingClient) {
        const response = await api.put(`/clients/${editingClient.id}`, payload);
        console.log('Update response:', response.data);
        toast.success('Client updated successfully');
      } else {
        const response = await api.post('/clients', payload);
        console.log('Create response:', response.data);
        toast.success(`Client "${response.data.name}" created successfully`);
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      
      const errorMessage = error.response?.data?.detail || 'Failed to save client';
      
      // Handle specific errors
      if (errorMessage.toLowerCase().includes('client code already exists')) {
        setFormErrors({ client_code: 'Client code already in use' });
        toast.error('Client code already in use');
      } else if (error.response?.status === 409) {
        setFormErrors({ client_code: 'Client code already in use' });
        toast.error('Client code already in use');
      } else if (error.response?.status === 400) {
        toast.error(errorMessage);
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`Are you sure you want to delete "${clientName}"?`)) return;
    
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success(`Client "${clientName}" deleted`);
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      client_code: client.client_code || client.code || '',
      contact_name: client.contact_name || '',
      contact_phone: client.contact_phone || '',
      contact_email: client.contact_email || '',
      address: client.address || '',
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.client_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            CLIENT MANAGEMENT
          </h1>
          <p className="text-sm text-zinc-500">{clients.length} registered clients</p>
        </div>
        
        {isAdmin && (
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) {
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold rounded-sm shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                data-testid="create-client-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                NEW CLIENT
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">
                  {editingClient ? 'EDIT CLIENT' : 'NEW CLIENT'}
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {editingClient ? 'Update client details below.' : 'Fill in the details to create a new client.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">
                      Client Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({...formData, name: e.target.value});
                        if (formErrors.name) setFormErrors({...formErrors, name: null});
                      }}
                      placeholder="Client name"
                      className={`bg-[#09090b] border-[#3f3f46] ${formErrors.name ? 'border-red-500' : ''}`}
                      disabled={isSubmitting}
                      data-testid="client-name-input"
                    />
                    {formErrors.name && (
                      <p className="text-xs text-red-400">{formErrors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">
                      Code <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      value={formData.client_code}
                      onChange={(e) => {
                        setFormData({...formData, client_code: e.target.value.toUpperCase()});
                        if (formErrors.client_code) setFormErrors({...formErrors, client_code: null});
                      }}
                      placeholder="ABBR"
                      className={`bg-[#09090b] border-[#3f3f46] font-mono ${formErrors.client_code ? 'border-red-500' : ''}`}
                      maxLength={10}
                      disabled={isSubmitting}
                      data-testid="client-code-input"
                    />
                    {formErrors.client_code && (
                      <p className="text-xs text-red-400">{formErrors.client_code}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Contact Name</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      placeholder="Primary contact"
                      className="bg-[#09090b] border-[#3f3f46]"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Contact Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      placeholder="+61 4xx xxx xxx"
                      className="bg-[#09090b] border-[#3f3f46]"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => {
                      setFormData({...formData, contact_email: e.target.value});
                      if (formErrors.contact_email) setFormErrors({...formErrors, contact_email: null});
                    }}
                    placeholder="contact@example.com"
                    className={`bg-[#09090b] border-[#3f3f46] ${formErrors.contact_email ? 'border-red-500' : ''}`}
                    disabled={isSubmitting}
                  />
                  {formErrors.contact_email && (
                    <p className="text-xs text-red-400">{formErrors.contact_email}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Address</Label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(address) => setFormData({...formData, address})}
                    onSelect={(addressData) => {
                      const selectedAddress =
                        addressData.full_address ||
                        addressData.formatted_address ||
                        addressData.address ||
                        formData.address;

                      setFormData({...formData, address: selectedAddress});
                    }}
                    placeholder="Start typing business address..."
                    disabled={isSubmitting}
                    testId="client-address-input"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowCreateDialog(false);
                      resetForm();
                    }}
                    className="border-zinc-600"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-500 hover:bg-blue-600"
                    disabled={isSubmitting}
                    data-testid="submit-client-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {editingClient ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingClient ? 'Update Client' : 'Create Client'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#09090b] border-[#3f3f46] text-white"
              data-testid="client-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredClients.length === 0 ? (
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-8 text-center text-zinc-500">
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No clients found</p>
            {isAdmin && (
              <p className="text-sm mt-2">Click "NEW CLIENT" to add your first client</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Card 
              key={client.id} 
              className="bg-[#18181b] border-[#3f3f46] hover:border-blue-500/50 transition-colors"
              data-testid={`client-card-${client.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-blue-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-rajdhani text-zinc-200">
                        {client.name}
                      </CardTitle>
                      <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400 font-mono">
                        {client.client_code}
                      </Badge>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                        onClick={() => openEditDialog(client)}
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400"
                        onClick={() => handleDelete(client.id, client.name)}
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {client.address && (
                  <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{client.address}</p>
                )}
                
                {(client.contact_name || client.contact_email || client.contact_phone) && (
                  <div className="space-y-1 pt-2 border-t border-[#3f3f46]">
                    {client.contact_name && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <Users className="w-3 h-3" />
                        <span>{client.contact_name}</span>
                      </div>
                    )}
                    {client.contact_email && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <Mail className="w-3 h-3" />
                        <span className="font-mono">{client.contact_email}</span>
                      </div>
                    )}
                    {client.contact_phone && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <Phone className="w-3 h-3" />
                        <span className="font-mono">{client.contact_phone}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#3f3f46]">
                  <Badge className={`text-xs ${client.is_active !== false ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'}`}>
                    {client.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                  {client.created_at && (
                    <span className="text-xs text-zinc-600 font-mono">
                      Since {formatDateTime(client.created_at).split(',')[0]}
                    </span>
                  )}
                </div>

                <Button
                  type="button"
                  className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate(`/client/${client.id}/roster`)}
                  data-testid={`open-client-roster-${client.id}`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Open Client Roster
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
